import React, { useEffect, useState } from "react";
import { Bot, Inbox, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Switch } from "./ui/switch";

const inputCls = "w-full px-2.5 py-1.5 text-sm border border-norea-border rounded bg-white focus:ring-1 focus:ring-norea-primary outline-none";

function AddBox({ onDone }) {
  const [f, setF] = useState({ name: "", email: "", app_password: "", folder: "INBOX", active: true });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const go = async () => {
    setBusy(true);
    try { await api.post("/mailboxes", f); toast.success("Boîte connectée — seuls les nouveaux emails seront lus"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Connexion impossible"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 p-3 rounded border border-norea-border bg-norea-bg/40 grid grid-cols-2 gap-2" data-testid="mailbox-add-form">
      <input value={f.name} onChange={set("name")} placeholder="Nom (ex. SAV)" className={inputCls} data-testid="mailbox-name" />
      <input value={f.email} onChange={set("email")} placeholder="Adresse Gmail" className={inputCls} data-testid="mailbox-email" />
      <input value={f.app_password} onChange={set("app_password")} type="password" placeholder="Mot de passe d'application (16 caractères)" className={inputCls} data-testid="mailbox-password" />
      <input value={f.folder} onChange={set("folder")} placeholder="Dossier ou libellé (INBOX par défaut)" className={inputCls} data-testid="mailbox-folder" />
      <div className="col-span-2 flex justify-end"><button onClick={go} disabled={busy || !f.name || !f.email || !f.app_password} className="btn-primary text-sm" data-testid="mailbox-add-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Tester et connecter</button></div>
    </div>
  );
}

function Stats({ stats }) {
  const hours = Object.entries(stats || {}).slice(0, 6);
  if (!hours.length) return null;
  return (
    <details className="mt-4 text-xs text-norea-textMuted" data-testid="imap-stats">
      <summary className="cursor-pointer">Charge Gmail des 6 dernières heures (connexions, téléchargements)</summary>
      <table className="mt-2 w-full"><tbody>
        {hours.flatMap(([h, boxes]) => Object.entries(boxes).map(([b, v]) => (
          <tr key={h + b} className="border-b border-norea-border"><td className="py-1 tabular-nums">{h.slice(11)}h</td><td>{b}</td>
            <td className="tabular-nums">{v.logins || 0} connexion(s)</td><td className="tabular-nums">{v.telechargements || 0} email(s)</td><td className="tabular-nums">{Math.round((v.octets || 0) / 1e6)} Mo</td></tr>
        )))}
      </tbody></table>
    </details>
  );
}

export default function MailboxesCard() {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/mailboxes").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (b, active) => {
    try { await api.put(`/mailboxes/${b.id}/active`, { active }); toast.success(active ? "Lecture IA activée (nouveaux emails uniquement)" : "Lecture IA désactivée"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };
  const cycle = async (active) => {
    try { await api.put("/mailboxes/principale/cycle", { active }); toast.success(active ? "Relevé réactivé sur cet environnement" : "Relevé coupé sur cet environnement"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };
  const del = async (b) => { if (window.confirm(`Déconnecter ${b.email} ?`)) { await api.delete(`/mailboxes/${b.id}`); load(); } };
  if (!data) return null;
  return (
    <div className="card-flat p-6" data-testid="mailboxes-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-medium text-norea-text flex items-center gap-2"><Inbox className="w-5 h-5 text-norea-primary" /> Boîtes Gmail — lecture IA</h3>
          <p className="text-xs text-norea-textMuted mt-1 max-w-2xl">L'IA lit chaque nouvel email reçu (et ses pièces jointes), reconnaît l'expéditeur grâce aux familles de partenaires et rattache l'email au dossier. Rien n'est envoyé et aucun statut ne change sans votre validation.</p>
        </div>
        <button onClick={() => setAdding((a) => !a)} className="btn-secondary text-sm shrink-0" data-testid="mailbox-add"><Plus className="w-4 h-4" /> Ajouter une boîte</button>
      </div>
      <ul className="mt-4 divide-y divide-norea-border border border-norea-border rounded-md">
        {data.items.map((b) => (
          <li key={b.id} className="px-4 py-3 flex items-center gap-4" data-testid={`mailbox-${b.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-norea-text">{b.name} <span className="font-normal text-norea-textMuted">· {b.email || "non connectée"}</span></div>
              <div className="text-[11px] text-norea-textMuted">{b.role} · {b.folder}{b.last_error ? ` · ⚠ ${b.last_error}` : ""}{b.active && b.ai_since ? ` · lecture IA depuis le ${new Date(b.ai_since).toLocaleString("fr-FR")}` : ""}</div>
              {b.primary && <label className="mt-1 inline-flex items-center gap-2 text-[11px] text-norea-textMuted"><Switch checked={b.cycle_on} onCheckedChange={cycle} data-testid="mailbox-cycle-toggle" /> Relever cette boîte depuis cet environnement (couper sur l'un des deux si l'aperçu et la production la relèvent en double)</label>}
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-norea-text"><Bot className="w-4 h-4 text-violet-600" /> Lecture IA
              <Switch checked={!!b.active} disabled={b.primary && !b.connected} onCheckedChange={(v) => toggle(b, v)} data-testid={`mailbox-toggle-${b.id}`} /></label>
            {!b.primary && <button onClick={() => del(b)} className="p-1.5 text-norea-textMuted hover:text-red-700" data-testid={`mailbox-delete-${b.id}`}><Trash2 className="w-4 h-4" /></button>}
          </li>
        ))}
      </ul>
      {adding && <AddBox onDone={() => { setAdding(false); load(); }} />}
      <Stats stats={data.imap_stats} />
    </div>
  );
}
